import type { UseMutationOptions } from '@tanstack/react-query';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { HTTPError } from 'ky';

import { useWmgSession } from '~/modules/auth/hooks/useWmgSession';
import { LabelQueryKeys, UserQueryKeys } from '~settings/api/config/queryKeys';
import { BFFClient } from '~settings/api/lib/BFFClient';
import { getRequestHeaders } from '~settings/api/lib/headers';
import type { UserDetail } from '~settings/api/lib/schemas';
import type { UserDetailPayload } from '~settings/api/lib/types';

export const useCreateUser = (
  options?: UseMutationOptions<UserDetail, HTTPError, UserDetailPayload>,
) => {
  const session = useWmgSession();
  const queryClient = useQueryClient();

  const { onSuccess, ...restOptions } = options ?? {};

  return useMutation<UserDetail, HTTPError, UserDetailPayload>({
    mutationFn: async (userData) => {
      return BFFClient.post('users', {
        json: userData,
        headers: getRequestHeaders(session.token),
      }).json();
    },

    onSuccess: async (data, variables, ctx) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: UserQueryKeys.list({}) }),
        queryClient.invalidateQueries({
          queryKey: LabelQueryKeys.list({}),
        }),
      ]);

      if (onSuccess) {
        onSuccess(data, variables, ctx);
      }
    },
    onError: (error) => {
      console.error('Create user failed:', error);
    },
    ...restOptions,
  });
};
